import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  Portal,
  HStack,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import { MdClose } from 'react-icons/md';

const TutorialOverlay = ({ steps = [], isActive, onComplete, onSkip, onNext, onPrevious, hideSkip = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightPosition, setHighlightPosition] = useState(null);
  const observerRef = useRef(null);

  const overlayBgSvg = useColorModeValue('rgba(0, 0, 0, 0.48)', 'rgba(0, 0, 0, 0.64)');
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const textColorSecondary = useColorModeValue('gray.600', 'gray.400');
  const highlightBorder = useColorModeValue('brand.500', 'brand.400');

  useEffect(() => {
    if (!isActive || steps.length === 0) {
      setCurrentStep(0);
      setHighlightPosition(null);
      return;
    }

    // Prevent scrolling during tutorial
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const updatePosition = () => {
      const step = steps[currentStep];
      if (!step?.targetId) return;

      const element = document.getElementById(step.targetId);

      if (element) {
        const rect = element.getBoundingClientRect();
        setHighlightPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });

        // Scroll element into view if it's not fully visible
        const isInViewport = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth
        );

        if (!isInViewport) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });

          // Update position after scroll completes
          setTimeout(() => {
            const newRect = element.getBoundingClientRect();
            setHighlightPosition({
              top: newRect.top,
              left: newRect.left,
              width: newRect.width,
              height: newRect.height,
            });
          }, 300);
        }
      } else {
        // Element not found, retry after a short delay
        setTimeout(updatePosition, 100);
      }
    };

    // Initial position update with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(updatePosition, 150);

    // Set up MutationObserver to track DOM changes
    observerRef.current = new MutationObserver(updatePosition);
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Handle window resize and scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      // Restore scrolling
      document.body.style.overflow = originalOverflow;
    };
  }, [isActive, currentStep, steps]);

  const handleNext = () => {
    if (onNext) {
      onNext(currentStep);
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious(currentStep);
    }
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setCurrentStep(0);
    setHighlightPosition(null);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    setCurrentStep(0);
    setHighlightPosition(null);
    if (onSkip) onSkip();
  };

  if (!isActive || steps.length === 0 || !highlightPosition) {
    return null;
  }

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  // Calculate tooltip position with smart positioning to avoid off-screen
  const tooltipMaxWidth = 400;
  const padding = 8; // Padding around highlighted element
  const tooltipPadding = 16; // Padding from edges of viewport

  // Special handling - if step has a tooltipTargetId, use that for tooltip positioning
  let tooltipReferenceElement = highlightPosition;
  if (currentStepData.tooltipTargetId) {
    const tooltipTargetElement = document.getElementById(currentStepData.tooltipTargetId);
    if (tooltipTargetElement) {
      const targetRect = tooltipTargetElement.getBoundingClientRect();
      tooltipReferenceElement = {
        top: targetRect.top,
        left: targetRect.left,
        width: targetRect.width,
        height: targetRect.height,
      };
    }
  }

  // Calculate available space
  const spaceBelow = window.innerHeight - (tooltipReferenceElement.top + tooltipReferenceElement.height);
  const spaceAbove = tooltipReferenceElement.top;
  const spaceRight = window.innerWidth - tooltipReferenceElement.left;
  const spaceLeft = tooltipReferenceElement.left;

  // Determine vertical position (prefer below, use above if not enough space)
  let tooltipTop;
  if (spaceBelow > 200) {
    tooltipTop = tooltipReferenceElement.top + tooltipReferenceElement.height + 24;
  } else if (spaceAbove > 200) {
    tooltipTop = tooltipReferenceElement.top - 200 - 24; // Approximate tooltip height
  } else {
    tooltipTop = tooltipReferenceElement.top + tooltipReferenceElement.height + 24;
  }

  // Determine horizontal position (prefer left-aligned, adjust if would go off screen)
  let tooltipLeft = tooltipReferenceElement.left;
  if (spaceRight < tooltipMaxWidth + tooltipPadding) {
    // Would go off right edge - position to the left of element or right-align
    if (spaceLeft > tooltipMaxWidth + tooltipPadding) {
      tooltipLeft = tooltipReferenceElement.left + tooltipReferenceElement.width - tooltipMaxWidth;
    } else {
      // Center it if both sides are tight
      tooltipLeft = Math.max(tooltipPadding, (window.innerWidth - tooltipMaxWidth) / 2);
    }
  }

  // Ensure tooltip doesn't go off left edge
  tooltipLeft = Math.max(tooltipPadding, tooltipLeft);

  // Ensure tooltip doesn't go off top edge
  tooltipTop = Math.max(tooltipPadding, tooltipTop);

  // Ensure tooltip doesn't go off bottom edge
  const maxTooltipTop = window.innerHeight - 250 - tooltipPadding; // Approximate tooltip height
  tooltipTop = Math.min(tooltipTop, maxTooltipTop);

  return (
    <Portal>
      {/* Dark overlay with cutout for highlighted element */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={9998}
        pointerEvents="auto"
        onClick={(e) => e.stopPropagation()}
      >
        <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
          <defs>
            <mask id="tutorial-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={highlightPosition.left - padding}
                y={highlightPosition.top - padding}
                width={highlightPosition.width + (padding * 2)}
                height={highlightPosition.height + (padding * 2)}
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill={overlayBgSvg}
            mask="url(#tutorial-mask)"
          />
        </svg>
      </Box>

      {/* Highlight box */}
      <Box
        position="fixed"
        top={`${highlightPosition.top - padding}px`}
        left={`${highlightPosition.left - padding}px`}
        width={`${highlightPosition.width + (padding * 2)}px`}
        height={`${highlightPosition.height + (padding * 2)}px`}
        border="3px solid"
        borderColor={highlightBorder}
        borderRadius="8px"
        zIndex={9999}
        pointerEvents="none"
        animation="tutorialGlow 2s ease-in-out infinite"
        transition="all 0.4s ease-in-out"
        sx={{
          '@keyframes tutorialGlow': {
            '0%, 100%': {
              boxShadow: '0 0 15px 3px rgba(66, 153, 225, 0.6), 0 0 30px 6px rgba(66, 153, 225, 0.4), 0 0 45px 9px rgba(66, 153, 225, 0.2)',
            },
            '50%': {
              boxShadow: '0 0 20px 5px rgba(66, 153, 225, 0.8), 0 0 40px 10px rgba(66, 153, 225, 0.6), 0 0 60px 15px rgba(66, 153, 225, 0.3)',
            },
          },
        }}
      />

      {/* Tooltip */}
      <Box
        position="fixed"
        top={`${tooltipTop}px`}
        left={`${tooltipLeft}px`}
        maxW={`${tooltipMaxWidth}px`}
        bg={tooltipBg}
        border="1px solid"
        borderColor={tooltipBorder}
        borderRadius="12px"
        p="20px"
        zIndex={10000}
        boxShadow="xl"
        transition="all 0.4s ease-in-out"
      >
        <Flex justify="space-between" align="start" mb="12px">
          <Box flex="1">
            <Text fontSize="sm" fontWeight="700" color={textColor} mb="8px">
              {currentStepData.title}
            </Text>
            <Text fontSize="sm" color={textColorSecondary} lineHeight="1.5">
              {currentStepData.description}
            </Text>
          </Box>
          <IconButton
            icon={<Icon as={MdClose} />}
            size="xs"
            variant="ghost"
            onClick={handleSkip}
            aria-label="Close tutorial"
            ml="8px"
          />
        </Flex>

        <Flex justify="space-between" align="center" mt="16px">
          <Text fontSize="xs" color={textColorSecondary}>
            {currentStep + 1} of {steps.length}
          </Text>
          <HStack spacing={2}>
            {currentStep > 0 && (
              <Button size="sm" variant="ghost" onClick={handlePrevious}>
                Previous
              </Button>
            )}
            {!hideSkip && !isLastStep && (
              <Button size="sm" variant="outline" onClick={handleSkip}>
                Skip Tour
              </Button>
            )}
            <Button size="sm" colorScheme="brand" onClick={handleNext}>
              {isLastStep ? 'Finish' : 'Next'}
            </Button>
          </HStack>
        </Flex>
      </Box>
    </Portal>
  );
};

export default TutorialOverlay;
